"use client";

import { OpenFeature, type JsonValue } from "@openfeature/web-sdk";
import { createContext, useContext, useEffect, useState } from "react";

export type FeatureFlagsClientState = {
  configured: boolean;
  ready: boolean;
  contextKey: string;
  error?: string;
};

export const FeatureFlagsClientContext = createContext<FeatureFlagsClientState>({
  configured: false,
  ready: false,
  contextKey: "unconfigured",
});

export function useFeatureFlag(flagKey: string, fallback = false) {
  return useBooleanFeatureFlag(flagKey, fallback);
}

const readBooleanFlag = (client: ReturnType<typeof OpenFeature.getClient>, flagKey: string, fallback: boolean) => client.getBooleanValue(flagKey, fallback);
const readStringFlag = (client: ReturnType<typeof OpenFeature.getClient>, flagKey: string, fallback: string) => client.getStringValue(flagKey, fallback);
const readNumberFlag = (client: ReturnType<typeof OpenFeature.getClient>, flagKey: string, fallback: number) => client.getNumberValue(flagKey, fallback);
const readObjectFlag = <T extends JsonValue>(client: ReturnType<typeof OpenFeature.getClient>, flagKey: string, fallback: T) => client.getObjectValue(flagKey, fallback) as T;

function useFlagValue<T extends JsonValue>(
  flagKey: string,
  fallback: T,
  read: (client: ReturnType<typeof OpenFeature.getClient>, flagKey: string, fallback: T) => T,
) {
  const runtime = useContext(FeatureFlagsClientContext);
  const [value, setValue] = useState<T>(fallback);

  useEffect(() => {
    if (!runtime.ready) {
      setValue(fallback);
      return;
    }

    try {
      setValue(read(OpenFeature.getClient(), flagKey, fallback));
    } catch {
      setValue(fallback);
    }
  }, [fallback, flagKey, read, runtime.contextKey, runtime.ready]);

  return value;
}

export function useBooleanFeatureFlag(flagKey: string, fallback = false) {
  return useFlagValue(flagKey, fallback, readBooleanFlag);
}

export function useStringFeatureFlag(flagKey: string, fallback: string) {
  return useFlagValue(flagKey, fallback, readStringFlag);
}

export function useNumberFeatureFlag(flagKey: string, fallback: number) {
  return useFlagValue(flagKey, fallback, readNumberFlag);
}

export function useObjectFeatureFlag<T extends JsonValue>(flagKey: string, fallback: T) {
  return useFlagValue(flagKey, fallback, readObjectFlag);
}
